import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Settings, Gift, Save, Zap, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettings() {
    const [s, setS] = useState(null);
    const [promoActive, setPromoActive] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await adminApi.get("/admin/settings");
            setS(data.settings);
            setPromoActive(data.promo_active_now);
        } catch (e) { toast.error("Couldn't load settings"); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await adminApi.put("/admin/settings", s);
            setS(data.settings);
            setPromoActive(data.promo_active_now);
            toast.success("Settings saved — live for all users");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    if (!s) return <div className="text-white/50 text-sm">Loading…</div>;

    const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

    return (
        <div data-testid="admin-settings-page" className="max-w-2xl">
            <h1 className="font-display text-3xl font-black flex items-center gap-2">
                <Settings className="h-7 w-7 text-[#E85D5D]" /> Site settings
            </h1>
            <p className="text-sm text-white/50 mt-1">Changes apply live to every user. No redeploy needed.</p>

            {/* Free promo */}
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="flex items-center gap-2 font-bold"><Gift className="h-4 w-4 text-[#E85D5D]" /> Free launch promo</div>
                <p className="text-xs text-white/50 mt-1">While enabled, everyone gets Premium free and checkout is disabled.</p>
                <label className="mt-4 flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!s.free_promo_enabled}
                        onChange={(e) => set("free_promo_enabled", e.target.checked)}
                        data-testid="settings-promo-enabled"
                        className="h-5 w-5 accent-[#E85D5D]"
                    />
                    <span className="text-sm font-semibold">Free promo enabled</span>
                    <span className={`text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 ${promoActive ? "bg-[#22C55E]/20 text-[#22C55E]" : "bg-white/10 text-white/50"}`}>
                        {promoActive ? "active now" : "off"}
                    </span>
                </label>
                <div className="mt-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/50">Promo ends (leave blank = no end date)</label>
                    <input
                        type="datetime-local"
                        value={(s.free_promo_until || "").slice(0, 16)}
                        onChange={(e) => set("free_promo_until", e.target.value ? new Date(e.target.value).toISOString() : "")}
                        data-testid="settings-promo-until"
                        className="mt-1 h-11 w-full rounded-xl bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-[#E85D5D]"
                    />
                </div>
            </div>

            {/* Pricing + limits */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <NumField icon={Zap} label="Premium price (₹/mo)" value={s.premium_price_inr} onChange={(v) => set("premium_price_inr", v)} testId="settings-price" />
                <NumField icon={Zap} label="Free swipes / day" value={s.free_swipes_per_day} onChange={(v) => set("free_swipes_per_day", v)} testId="settings-swipes" />
                <NumField icon={HomeIcon} label="Free listings" value={s.free_listings} onChange={(v) => set("free_listings", v)} testId="settings-listings" />
            </div>

            <button
                onClick={save}
                disabled={saving}
                data-testid="settings-save-btn"
                className="mt-6 h-12 rounded-2xl bg-[#E85D5D] hover:bg-[#E85D5D]/90 px-6 font-black flex items-center gap-2 disabled:opacity-50"
            >
                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save settings"}
            </button>
        </div>
    );
}

function NumField({ icon: Icon, label, value, onChange, testId }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50 flex items-center gap-1">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <input
                type="number"
                min="0"
                value={value ?? 0}
                onChange={(e) => onChange(e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
                data-testid={testId}
                className="mt-2 h-11 w-full rounded-xl bg-black/30 border border-white/10 px-3 font-display text-2xl font-black outline-none focus:border-[#E85D5D]"
            />
        </div>
    );
}
